<template>
  <main class="privacy-page">
    <div class="container">
      <h3>
        Last updated: 5 November 2026
        <span>(version 1.6)</span>
      </h3>
      <h1>Proxify's Privacy Policy</h1>
      <div class="privacy-content">
        <h3>Why and who?</h3>

        <p>
          Proxify cares about privacy and protecting the Personal Data handled
          by us. This means that we care about your personal integrity and
          actively work to protect it.
        </p>
        <p>
          In this Policy, we overall describe how and the purposes for which we
          use your Personal Data as well as what lawful basis we use and what
          measures we take to protect Personal Data. We also provide information
          on how you exercise the rights you have linked to our Processing of
          Personal data.
        </p>
        <p>
          Proxify AB, registration number 559017-0733, ("Proxify", "we", "us",
          "our") is the Controller of all Personal Data listed in this Privacy
          Policy (the "Policy").
        </p>
        <p>
          This Policy provides information on how we handle Personal Data when
          you communicate with us, use the Services or visit our website
          proxify.io & career.proxify.io (together the "Functions").
        </p>

        <h4>
          The intended recipient of the information provided in this Policy is:
        </h4>

        <ul>
          <li>Users of the Services</li>
          <li>Potential customers</li>
          <li>Visitors of our website</li>
          <li>Contractor applicants</li>
        </ul>
        <h3>Definitions</h3>

        <p>
          "Applicable Law" refers to the legislation applicable to the
          Processing of Personal Data, including the GDPR, supplementary
          national legislation, as well as practices, guidelines and
          recommendations issued by a national or EU supervisory authority.
        </p>
        <p>
          "Controller" is the company/organisation that decides for what
          purposes and in what way personal data is to be processed and is
          responsible for the Processing of Personal Data in accordance with
          Applicable Law.
        </p>
        <p>
          "Data Subject" is the living, natural person whose Personal Data is
          being processed.
        </p>
        <p>
          "Personal Data" is all information relating, directly or indirectly,
          to an identifiable natural person.
        </p>
        <p>
          "Processing" means any operation or set of operations which is
          performed on Personal data, e.g. storage, modification, reading,
          handover and similar.
        </p>
        <p>
          "Processor" is the company/organisation that processes Personal Data
          on behalf of the Controller and can therefore only process the
          Personal Data according to the instructions of the Controller and the
          Applicable Law.
        </p>
        <p>
          "The Services" are to provide companies with highly skilled tech
          talents as well as providing opportunities for talents in search of a
          remote job.
        </p>
        <p>
          "Contractor applicants" are tech talents that want to become part of
          the Proxify contractors talent pool.
        </p>
        <p>
          The definitions above shall apply in the Policy regardless if they are
          capitalised or not.
        </p>

        <h3>Proxify's role as a Controller</h3>

        <p>
          The information in this Policy covers Personal Data Processing for
          which Proxify is the Controller. As a Controller we are responsible
          for the Processing for which we decide the purpose of ("the why") and
          the means for the Processing (what methods, what Personal Data and for
          how long it is stored. The Policy does not describe how we Process
          Personal Data in the role of a Processor - i.e. when we process
          Personal Data on behalf of our customers.
        </p>
        <p>
          We are providing a platform where we match tech talent with companies
          in need of adding people to their tech teams. We need to process
          information in order to be able to reach out to people actively
          showing interest in our services (to find talent or to apply to become
          a member of our network), create user accounts, match clients with
          talents, handle time reporting, invoicing etc. In some cases, we use
          personal data for marketing purposes by sending out information or
          offers.
        </p>

        <h3>Proxify's Processing of Personal Data</h3>

        <p>
          We have a responsibility to describe and demonstrate how we fulfill
          the requirements that are imposed on us when we Process your Personal
          Data. This section aims to give you an understanding of what type of
          Personal Data we Process and for what reasons.
        </p>

        <h4>For how long do we store your Personal Data?</h4>

        <p>
          We will keep your Personal Data as long as it is necessary for the
          purpose for which it was collected. Depending on the lawful basis on
          which we support the Processing, this may a) be regulated in a
          contract, b) be dependent on valid consent, c) be stated in
          legislation or d) follow by an internal assessment based on a
          legitimate interest assessment (LIA). In the list below, we indicate,
          where possible, the period during which the Personal Data will be
          stored and the criteria used to determine the storage period.
        </p>
        <p>
          We never store your Personal Data longer than necessary and delete
          Personal Data regularly. Proxify also takes reasonable actions to keep
          the Personal Data being Processed updated and to delete outdated and
          otherwise incorrect or redundant Personal Data. Logs of AI assistant
          (MCP) connections are kept for 30 days.
        </p>

        <h4>Processing</h4>

        <p>
          The main purpose of the data processing undertaken by us is to
          provide, carry out and improve our services to you. There are several
          different reasons why we may need to collect, manage and save your
          data.
        </p>
        <p>We mainly Process the following types of Personal Data:</p>

        <ul>
          <li>
            Personal data ie contact details to be able to confirm your
            identity, to verify your personal and contact details and to be able
            to communicate with you;
          </li>
          <li>
            Information on your usage of the service or product to improve our
            services provided to you;
          </li>
          <li>
            IP-address to perform customer analysis and for content on our site
            to be presented effectively to you and the device you use;
          </li>
          <li>
            In case of an application to become a member of the network, the
            data you provide in the application process and in the electronic
            programs Proxify uses in the assessment (i.e. automated CV parsing
            and profile analysis tools, call recording, note-taking, analytics
            platforms, CRM systems, and external assessment tools).
          </li>
        </ul>

        <h4>AI-assisted recruitment processing</h4>

        <p>
          Proxify uses Teamtailor AB (Sweden) as its recruitment management
          system (ATS) for contractor applicants. Teamtailor acts as our
          Processor.
        </p>
        <p>
          Proxify has connected Claude, an AI assistant provided by Anthropic
          Ireland, Limited, to Teamtailor through a read-only connection.
          Recruiters use Claude on request to summarise and cross-reference
          applications and prepare overviews for recruiter review. Claude cannot
          change records, move applicants between stages, message applicants or
          reject applicants. The AI tool reads data already held in Teamtailor;
          no new data is collected.
        </p>
        <p>
          The AI tool may read the following categories of Personal Data held in
          Teamtailor:
        </p>

        <ul>
          <li>Name, contact details, and profile information</li>
          <li>CV, work history, education, skills, and languages</li>
          <li>
            Application answers, including salary expectations and answers about
            relocation and languages
          </li>
          <li>
            Screening call transcripts, where the call was recorded with your
            knowledge
          </li>
          <li>Recruiter notes, scorecards, and evaluations</li>
          <li>Application history and stage information</li>
          <li>Correspondence held in Teamtailor</li>
        </ul>
        <p>
          If you decline recording, no screening call transcript exists to be
          processed. Candidate data is not used to train AI models; this is
          contractually excluded under Anthropic's Commercial Terms of Service.
        </p>

        <h4>Connecting Proxify to an AI assistant</h4>

        <p>
          You can connect your Proxify account to an AI assistant that supports
          the Model Context Protocol (MCP), such as Claude, ChatGPT or Gemini.
          Nothing is shared until you sign in and select "Allow access".
        </p>
        <p>
          What the AI assistant receives: only what you can see in Proxify.
          Clients: engagements, team members, check-ins, hours and time reports;
          client admins also see rates and invoices. Members: their own profile,
          applications, engagement terms, check-ins, hours and time reports.
          Proxify staff: what their role can see. Members can also ask the
          assistant to file a check-in or update their availability and status.
        </p>
        <p>
          Who receives it: the AI provider you choose, under that provider's own
          terms and your plan with it. Member information that a client can see,
          such as check-ins, may reach the AI provider chosen by that client.
          Check-ins are passed on as written, including any health details they
          contain.
        </p>
        <p>
          What we keep: a log of which tool ran, for whom, when and whether it
          worked, for 30 days. We do not store the full request. We record each
          time access is allowed. We also record use of the connection in our
          product analytics.
        </p>
        <p>
          Ending the connection: clients and members can disconnect at any time
          in Settings. Proxify staff disconnect in their AI assistant. Proxify
          can also switch the connection off. Disconnecting stops new sharing.
          It does not remove what the AI provider has already received.
        </p>

        <h4>How do we get access to your Personal Data?</h4>

        <p>
          We collect your Personal Data in a number of different ways. We mainly
          get access to your Personal Data:
        </p>

        <ul>
          <li>By you providing your Personal Data to us</li>
          <li>Through third-party analysis technology ex. Cookies</li>
          <li>
            Through automated tools that extract and structure information from
            documents you submit (such as your CV)
          </li>
        </ul>
        <h4>Lawful basis</h4>

        <p>
          In order for us to be able to process your Personal Data, it is
          required that we have so-called legal basis for each process. In our
          business, we process your personal data mainly on the following
          grounds:
        </p>
        <p>
          Performance of a contract - The Processing is necessary for the
          performance of a contract entered between us and the Data Subject, or
          to prepare for entering into an agreement with the Data Subject.
        </p>
        <p>
          Legitimate interest - Proxify may process Personal Data if we have
          assessed that a legitimate interest overrides the interest of
          fundamental rights and freedoms of the Data Subject, and if the
          processing is necessary for the purpose in question.
        </p>
        <p>
          Automated processing — Where we use automated tools (such as CV
          parsing or algorithmic profile analysis) as part of the application
          and vetting process, we do so on the basis of pre-contractual steps
          (Article 6(1)(b)) or our legitimate interest in efficiently evaluating
          applications at scale (Article 6(1)(f)). We do not make decisions
          producing legal or similarly significant effects based solely on
          automated processing without appropriate safeguards. You have the
          right to request human intervention, express your point of view, and
          contest any such decision by contacting compliance@proxify.io.
        </p>
        <p>
          Our AI-assisted review via Teamtailor and Claude is based on our
          legitimate interest under Article 6(1)(f) GDPR in the efficient and
          thorough evaluation of applications. We have carried out a Legitimate
          Interest Assessment. AI outputs are support only, are reviewed and
          verified for accuracy by a recruiter, and every selection decision
          made using this tool is taken by a person.
        </p>
        <p>
          Connecting Proxify to an AI assistant (MCP): for clients and members,
          this Processing is part of the Services they already use and is
          necessary for the performance of our contract with them (Article
          6(1)(b) GDPR); no new agreement is required. For Proxify staff, the
          Processing is based on our legitimate interest under Article 6(1)(f)
          GDPR, as Proxify needs it for its work.
        </p>

        <h4>Contractor Applicants</h4>

        <p>
          When you apply to be added as a contractor to Proxify talent pool, we
          process your personal data to manage your application. The lawful
          basis for this processing is to take steps at your request prior to
          entering into a potential service contract.
        </p>
        <p>
          If your application is unsuccessful, we retain your Personal Data for
          up to 24 months from the later of the final application decision or
          your last interaction with us. Before the 24-month period expires, our
          recruitment system will send you an automated email asking you to
          actively confirm (opt back in) if you want us to keep your data. If
          you do not confirm, your data will be deleted. The lawful basis for
          this retention is our legitimate interest (as per GDPR Article
          6(1)(f)) to:
        </p>
        <ul>
          <li>
            Manage our application process effectively and prevent duplicate or
            fraudulent applications.
          </li>
          <li>
            Protect our legal rights and defend against potential legal claims,
            for example, under the Swedish Discrimination Act.
          </li>
        </ul>
        <p>
          If your application is successful or remains under consideration, we
          retain your personal data for as long as necessary to manage the
          ongoing relationship. The lawful basis for this processing is the
          performance of pre-contractual steps at your request (GDPR Article
          6(1)(b)) and, where applicable, our legitimate interest in maintaining
          an effective talent pool (GDPR Article 6(1)(f)).
        </p>
        <p>
          The data we retain includes your application documents (CV, cover
          letter), assessment results, and our internal notes. This data is held
          securely with restricted access and will not be used for any other
          purpose, such as marketing. Data read by the AI tool is held by
          Anthropic only for the purposes of providing the service, for no
          longer than the term of our agreement with Anthropic, and is deleted
          within 30 days of that agreement ending. After the lawful retention
          time has expired, your data can be deleted from our systems.
        </p>

        <h3>Your rights</h3>

        <p>
          You are the one in control of your Personal Data and we always strive
          to ensure that you can exercise your rights as efficiently and
          smoothly as possible.
        </p>
        <p>
          Access - You always have the right to receive information about the
          Processing of data that concerns you. We only provide information if
          we have been able to verify that it is you that are requesting the
          information.
        </p>
        <p>
          Rectification - If you find that the Personal Data we process about
          you is incorrect, let us know and we will fix it!
        </p>
        <p>
          Erasure - Do you want us to completely forget about you? You have the
          right to be forgotten and request deletion of your Personal Data when
          the Processing is no longer necessary for the purpose for which it was
          collected. If we are required to retain your information under
          applicable law or a contract that we have entered with you, we will
          ensure that it is processed only for the specific purpose set forth in
          such applicable law or contract. We will thereafter erase the
          information as soon as possible. Please note that in some cases, we
          may have a legal obligation or a compelling legitimate interest to
          retain your data for a limited period, even if you request erasure.
          For example, data from unsuccessful contractor applications is
          retained for two years for legal and administrative purposes, as
          detailed in our policy.
        </p>
        <p>
          Objections - Do you disagree with our assessment that a legitimate
          interest for Processing your Personal Data overrides your interest in
          protecting your privacy? Don't worry - in such case, we will review
          our legitimate interest assessment. Of course, we add your objection
          to the balance and make a new assessment to see if we can still
          justify our Processing of your Personal Data. If you object to direct
          marketing, we will immediately delete your personal information
          without making an assessment. If you object to our processing of your
          data based on our legitimate interest, such as the retention of your
          contractor application data, we will assess your objection. We may be
          required to continue processing if we can demonstrate compelling
          legitimate grounds that override your interests, rights, and freedoms.
        </p>

        <p>
          Restriction - You can also ask us to restrict our Processing of your
          Personal Data
        </p>

        <ul>
          <li>
            Whilst we are Processing a request from you for any of your other
            rights;
          </li>
          <li>
            If, instead of requesting erasure, you want us to limit the
            Processing of Personal Data for a specific purpose. For example, if
            you do not want us to send advertising to you in the future, we
            still need to save your name in order to know that we should not
            contact you; or
          </li>
          <li>
            In cases where we no longer need the information in relation to the
            purpose for which it was collected, provided that you do not have an
            interest in retaining it to make a legal claim.
          </li>
        </ul>
        <p>
          Data portability - We may provide you with the data that you have
          submitted to us or that we have received from you in connection with a
          contract that we have entered with you. You will receive your
          information in a commonly used and machine-readable format that you
          can transfer to another personal data manager.
        </p>
        <p>
          Withdraw consent - If you have given consent to one or several
          specific Processing(s) of your Personal Data, you have the right to
          withdraw your consent at any time and thus ask us to terminate the
          Processing immediately. Please note that you can only withdraw your
          consent for future Processing of Personal Data and not for Processing
          that has already taken place.
        </p>

        <h4>How you use your rights</h4>

        Reach out to compliance@proxify.io

        <h3>Transfer of personal data</h3>

        <p>
          In order to run our business, we may need help from others who will
          process Personal Data on our behalf, so-called Processors.
        </p>
        <p>
          In cases where our Processors transfer Personal Data outside the
          EU/EEA, we have ensured that the level of protection is adequate, and
          in compliance with Applicable Law, by controlling that either of the
          following requirements are fulfilled:
        </p>

        <ul>
          <li>
            the EU Commission has determined that the level of protection is
            adequate in the third country where the data is processed;
          </li>
          <li>
            the Processor has signed up to the EU Commission's standard contract
            clauses (SCCs) for data transfer to non-EU/EEA countries; or
          </li>
          <li>
            the Processor has taken other appropriate safeguards prior to the
            transfer and that such safeguards comply with Applicable law.
          </li>
        </ul>
        <p>
          We have entered into Data Processing Agreements (DPA) with all our
          Processors. The DPA sets out, among other things, how the Processor
          may process the Personal Data and what security measures are required
          for the Processing.
        </p>
        <p>
          Our AI providers are Anthropic Ireland, Limited and OpenAI Ireland
          Ltd. Data may be processed on infrastructure outside the EU/EEA,
          including the United States. Such transfers are covered by the EU
          Commission's Standard Contractual Clauses (Module Two, controller to
          processor), incorporated in our data processing agreements with
          Anthropic and OpenAI. Teamtailor may use sub-processors outside the
          EU/EEA relying on the EU-US Data Privacy Framework or SCCs.
        </p>
        <p>
          Information an AI assistant receives from Proxify goes to the AI
          provider you choose, which may process it outside the EU/EEA under its
          own terms.
        </p>
        <p>
          We may also need to disclose your personal information to certain
          designated authorities in order to fulfill obligations under
          applicable law or legally binding judgements.
        </p>

        <h4>Our processors</h4>

        <p>
          Proxify does not sell your Personal Data to third parties and of
          course we do not share your Personal Data with just anyone. However,
          in some cases we may need to share your Personal Data with selected
          third parties. If so, we make sure that the transfer happens in a
          secure way that protects your privacy. To follow are categories of
          recipients with whom we may share your data.
        </p>

        <ul>
          <li>
            AI and automation service providers — for CV parsing, profile
            analysis, and automated matching including Mistral SAS (reading
            CVs), OpenAI Ireland Ltd. (profile summaries and matching) and
            Anthropic Ireland, Limited (Claude), used to support recruiters in
            reviewing applications held in our recruitment system. Proxify staff
            also use Claude and ChatGPT with AI assistants connected to Proxify
          </li>
          <li>
            Recruitment management system provider — Teamtailor AB (Sweden), for
            managing applications, profiles, interview records and
            communications with contractor applicants
          </li>
          <li>
            Analytics and tracking providers — for monitoring platform usage and
            improving the application experience
          </li>
          <li>
            Assessment and evaluation providers — for conducting skills
            assessments, recorded interviews, and technical evaluations as part
            of the vetting process
          </li>
          <li>
            CRM and marketing platforms — for managing applicant communications
            and coordinating the vetting pipeline
          </li>
          <li>
            Payment and invoicing platforms — for processing payments to
            contractors
          </li>
          <li>
            IT infrastructure and business system suppliers — for hosting, data
            storage, and case management
          </li>
          <li>Advertising agencies and suppliers of print and advertising</li>
        </ul>

        <h3>Use of AI in our recruitment process (EU AI Act)</h3>

        <p>
          Proxify uses AI tools in its recruitment and selection process for
          contractors. Proxify maintains human oversight, informs applicants
          through this Policy and a separate applicant notice, and applicants
          can request human review, express their view and contest a decision
          via compliance@proxify.io.
        </p>

        <h3>Security measures</h3>

        <p>
          Proxify has taken technical and organisational measures to ensure that
          your Personal Data is processed securely and protected from loss,
          abuse and unauthorised access.
        </p>

        <h4>Our security measures</h4>

        <p>
          Organisational security measures are measures that are implemented in
          work methods and routines within the organisation.
        </p>

        <ul>
          <li>Internal governance documents (policies/instructions)</li>
          <li>Login and password management</li>
          <li>Physical security (premises etc.)</li>
        </ul>
        <p>
          Technical security measures are measures implemented through technical
          solutions.
        </p>

        <ul>
          <li>Encryption</li>
          <li>Access log</li>
          <li>Secure network</li>
          <li>VPN</li>
          <li>Back-up</li>
          <li>Regular security inspection</li>
          <li>Two-step verification</li>
        </ul>

        <h3>GDPR Consent</h3>

        <p>
          We want to ensure transparency and protection of your privacy. The
          legal basis for processing the contractor application is performance
          of a contract/pre-contractual steps.
        </p>
        <p>
          By proceeding with the first step of the application form, you are
          actively checking the box with and thus acknowledging and providing
          consent for the processing of your personal data in accordance with
          this Privacy Policy.
        </p>
        <p>
          By submitting your application, you acknowledge that your data will be
          processed in accordance with our Privacy Policy.
        </p>
        <ol>
          <li>
            Data Processing:
            <p>
              Proxify collects and processes personal data provided by users for
              the sole purpose of improving the overall experience of our
              services. This includes, but is not limited to, information such
              as your name, email address, and usage patterns.
            </p>
          </li>
          <li>
            Newsletter Subscription:
            <p>
              By accepting these terms in the application form, you agree to be
              automatically subscribed to our newsletter. The newsletter may
              contain updates, promotions, and other relevant information about
              Proxify. You have the right to unsubscribe from the newsletter at
              any time by following the "unsubscribe" link provided in each
              email.
            </p>
          </li>
          <li>
            Data Usage:
            <p>
              Your personal data will be used exclusively by Proxify to enhance
              and personalize your experience with our services. We may analyze
              user data to improve our platform's functionality, security, and
              overall performance.
            </p>
          </li>
          <li>
            Data Security:
            <p>
              Proxify is committed to ensuring the security and confidentiality
              of your personal data. We implement industry-standard measures to
              protect against unauthorized access, disclosure, alteration, and
              destruction of your information.
            </p>
          </li>
          <li>
            Data Retention:
            <p>
              For contractor applicants, we retain Personal Data for up to 24
              months from the later of the final application decision or your
              last interaction with our platform. Before the 24-month period
              expires, our recruitment system will send you an automated email
              asking you to actively confirm (opt back in) if you want your data
              kept. If you do not confirm, the data will be deleted. Data read
              by the AI tool is held by Anthropic only for the purposes of
              providing the service, for no longer than the term of our
              agreement with Anthropic, and is deleted within 30 days of that
              agreement ending. For all other users, we retain personal data
              only for as long as necessary to fulfill the purposes outlined in
              this Privacy Policy unless a longer retention period is required
              by law.
            </p>
          </li>
        </ol>

        <p>
          By proceeding with the first step of the application form, you
          acknowledge that you have read and understood our Privacy Policy and
          consent to the processing of your personal data as described herein.
        </p>

        <h3>Cookies</h3>

        <p>
          Proxify uses cookies and similar tracking techniques to analyse the
          use of the Functions so that we can give you the best user experience.
          For more information on how we use cookies, see our Cookie Policy (in
          the footer of the main site).
        </p>

        <h3>If we don't keep our promise</h3>

        <p>
          If you think that we are not Processing your Personal Data correctly,
          even after you have notified us of this, you are always entitled to
          submit your complaint to the Swedish Authority for Privacy Protection.
        </p>
        <p>
          More information about our obligations and your rights can be found at
          https://www.imy.se/
        </p>
        <p>You can contact the authority via e-mail at: imy@imy.se</p>

        <h3>Changes to this policy</h3>

        <p>
          We reserve the right to make changes to this Policy. In the event that
          the change affects our obligations or your rights, we will inform you
          about the changes in advance so that you are given the opportunity to
          take a position on the updated policy.
        </p>

        <h3>Contact</h3>

        <p>
          Please contact us if you have questions about your rights or if you
          have any other questions about how we process your personal
          information:
        </p>

        <p>compliance@proxify.io</p>
      </div>
    </div>
  </main>
</template>

<script>
export default {};
</script>

<style lang="scss" scoped>
@import './PrivacyPage.scss';
</style>
